// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	getCachedDesktopTroubleshootingSettings,
	getDesktopTroubleshootingSettings,
} from '@app/features/devtools/utils/DesktopTroubleshootingUtils';
import {
	getElectronAPI,
	getNativePlatform,
	isDesktop,
	supportsDesktopScreenShareAudioCapture,
} from '@app/features/ui/utils/NativeUtils';
import {createVoiceDiagnosticLogger, readVoiceDiagnosticsLog} from '@app/features/voice/diagnostics/VoiceDiagnostics';
import {
	uploadVoiceDiagnosticsArchive,
	type VoiceDiagnosticsUploadResponse,
} from '@app/features/voice/diagnostics/VoiceDiagnosticsApi';
import {getErrorAggregates} from '@app/features/voice/diagnostics/VoiceDiagnosticsInstrumentation';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {
	getCodecCapabilityReport,
	getLiveKitSupportedCodecs,
	selectOptimalCameraCodec,
	selectOptimalScreenShareCodec,
} from '@app/features/voice/utils/CodecCapabilityDetector';
import {loadGpuEncoderReport} from '@app/features/voice/utils/GpuEncoderCapabilities';
import {getNativeAudioBridgeStats} from '@app/features/voice/utils/NativeAudioCaptureBridge';
import {getDisplayShareEnvironment} from '@app/features/voice/utils/ScreenShareEnvironment';
import type {NativeAudioApplication, VirtmicNode, VoiceDiagnosticCrashReport} from '@app/types/electron.d';

const diagnostics = createVoiceDiagnosticLogger('app.VoiceDiagnosticsExport');
const EXPORT_TIMEOUT_MS = 3_000;
const MAX_INCLUDED_TARGETS = 200;

interface ZipEntry {
	name: string;

	content: string | Uint8Array;
	mimeType?: string;
}

function nowStamp(): string {
	return new Date().toISOString().replace(/[:.]/g, '-');
}

function safeError(error: unknown): Record<string, unknown> {
	if (error instanceof Error) {
		return {
			name: error.name,
			message: error.message,
			stack: error.stack ?? null,
		};
	}
	return {message: String(error)};
}

async function withTimeout<T>(
	label: string,
	task: Promise<T>,
	timeoutMs = EXPORT_TIMEOUT_MS,
): Promise<T | {error: string}> {
	let timeoutId: number | null = null;
	const timeout = new Promise<{error: string}>((resolve) => {
		timeoutId = window.setTimeout(() => resolve({error: `${label} timed out after ${timeoutMs}ms`}), timeoutMs);
	});
	try {
		return await Promise.race([task, timeout]);
	} catch (error) {
		return {error: `${label} failed: ${safeError(error).message ?? 'unknown error'}`};
	} finally {
		if (timeoutId != null) {
			window.clearTimeout(timeoutId);
		}
	}
}

function bytes(value: string): number {
	try {
		return new TextEncoder().encode(value).byteLength;
	} catch {
		return value.length;
	}
}

async function hashString(value: string | null | undefined): Promise<string | null> {
	if (!value) return null;
	try {
		const data = new TextEncoder().encode(value);
		const digest = await crypto.subtle.digest('SHA-256', data);
		return Array.from(new Uint8Array(digest))
			.map((byte) => byte.toString(16).padStart(2, '0'))
			.join('');
	} catch {
		let hash = 0;
		for (let index = 0; index < value.length; index += 1) {
			hash = (hash << 5) - hash + value.charCodeAt(index);
			hash |= 0;
		}
		return `fallback-${Math.abs(hash).toString(16)}`;
	}
}

async function queryPermission(name: string): Promise<string> {
	const permissions = navigator.permissions as
		| {query?: (descriptor: {name: string}) => Promise<PermissionStatus>}
		| undefined;
	if (!permissions?.query) return 'unsupported';
	try {
		return (await permissions.query({name})).state;
	} catch (error) {
		return `error:${safeError(error).message ?? 'unknown'}`;
	}
}

async function collectMediaDevices(): Promise<Record<string, unknown>> {
	if (!navigator.mediaDevices?.enumerateDevices) {
		return {supported: false};
	}

	try {
		const devices = await navigator.mediaDevices.enumerateDevices();
		return {
			supported: true,
			count: devices.length,
			devices: await Promise.all(
				devices.map(async (device) => ({
					kind: device.kind,
					label: device.label || '',
					hasLabel: Boolean(device.label),
					deviceIdHash: await hashString(device.deviceId),
					deviceIdLength: device.deviceId?.length ?? 0,
					groupIdHash: await hashString(device.groupId),
					groupIdLength: device.groupId?.length ?? 0,
					isDefaultDeviceId: device.deviceId === 'default',
				})),
			),
		};
	} catch (error) {
		return {supported: true, error: safeError(error)};
	}
}

function collectBrowserMetadata(): Record<string, unknown> {
	const userAgentData = (
		navigator as Navigator & {
			userAgentData?: {
				brands?: Array<{brand: string; version: string}>;
				mobile?: boolean;
				platform?: string;
				getHighEntropyValues?: (hints: Array<string>) => Promise<Record<string, unknown>>;
			};
		}
	).userAgentData;
	const memory = (
		performance as Performance & {
			memory?: {
				jsHeapSizeLimit?: number;
				totalJSHeapSize?: number;
				usedJSHeapSize?: number;
			};
		}
	).memory;

	return {
		userAgent: navigator.userAgent,
		platform: navigator.platform,
		userAgentData: userAgentData
			? {
					brands: userAgentData.brands ?? [],
					mobile: userAgentData.mobile ?? null,
					platform: userAgentData.platform ?? null,
				}
			: null,
		language: navigator.language,
		languages: Array.from(navigator.languages ?? []),
		hardwareConcurrency: navigator.hardwareConcurrency ?? null,
		deviceMemory: (navigator as Navigator & {deviceMemory?: number}).deviceMemory ?? null,
		maxTouchPoints: navigator.maxTouchPoints ?? null,
		cookieEnabled: navigator.cookieEnabled,
		onLine: navigator.onLine,
		doNotTrack: navigator.doNotTrack,
		secureContext: window.isSecureContext,
		visibilityState: document.visibilityState,
		location: {
			origin: window.location.origin,
			pathname: window.location.pathname,
			hash: window.location.hash,
		},
		viewport: {
			width: window.innerWidth,
			height: window.innerHeight,
			devicePixelRatio: window.devicePixelRatio,
		},
		screen: {
			width: window.screen?.width ?? null,
			height: window.screen?.height ?? null,
			availWidth: window.screen?.availWidth ?? null,
			availHeight: window.screen?.availHeight ?? null,
			colorDepth: window.screen?.colorDepth ?? null,
			pixelDepth: window.screen?.pixelDepth ?? null,
		},
		performanceMemory: memory ?? null,
	};
}

function collectWebRtcMetadata(): Record<string, unknown> {
	return {
		mediaDevices: Boolean(navigator.mediaDevices),
		getUserMedia: Boolean(navigator.mediaDevices?.getUserMedia),
		getDisplayMedia: Boolean(navigator.mediaDevices?.getDisplayMedia),
		enumerateDevices: Boolean(navigator.mediaDevices?.enumerateDevices),
		audioContext: typeof AudioContext !== 'undefined',
		webkitAudioContext: typeof (window as Window & {webkitAudioContext?: unknown}).webkitAudioContext !== 'undefined',
		mediaStreamTrackGenerator:
			typeof (window as Window & {MediaStreamTrackGenerator?: unknown}).MediaStreamTrackGenerator !== 'undefined',
		audioData: typeof (window as Window & {AudioData?: unknown}).AudioData !== 'undefined',
		rtcPeerConnection: typeof RTCPeerConnection !== 'undefined',
	};
}

async function collectCodecMetadata(): Promise<Record<string, unknown>> {
	const cameraPreference = VoiceSettings.getPreferredVideoCodec();
	const screenSharePreference = VoiceSettings.getPreferredScreenShareCodec();
	let report: ReturnType<typeof getCodecCapabilityReport> | null = null;
	let resolvedCameraCodec: string | null = null;
	let resolvedScreenShareCodec: string | null = null;
	try {
		report = getCodecCapabilityReport();
	} catch (error) {
		report = null;
		diagnostics.warn('export.codec_report_failed', {error: safeError(error)});
	}
	try {
		resolvedCameraCodec = selectOptimalCameraCodec(cameraPreference);
	} catch (error) {
		diagnostics.warn('export.codec_camera_resolution_failed', {error: safeError(error)});
	}
	try {
		resolvedScreenShareCodec = selectOptimalScreenShareCodec(screenSharePreference);
	} catch (error) {
		diagnostics.warn('export.codec_screen_share_resolution_failed', {error: safeError(error)});
	}

	let gpuReport = null;
	try {
		gpuReport = await loadGpuEncoderReport();
	} catch (error) {
		diagnostics.warn('export.gpu_report_failed', {error: safeError(error)});
	}

	return {
		liveKitSupported: getLiveKitSupportedCodecs(),
		preferences: {
			camera: cameraPreference,
			screenShare: screenSharePreference,
		},
		resolved: {
			camera: resolvedCameraCodec,
			screenShare: resolvedScreenShareCodec,
		},
		support: report,
		gpuHardwareEncode: gpuReport,
	};
}

async function collectVoiceSettingsMetadata(): Promise<Record<string, unknown>> {
	return {
		inputDeviceIdHash: await hashString(VoiceSettings.getInputDeviceId()),
		outputDeviceIdHash: await hashString(VoiceSettings.getOutputDeviceId()),
		videoDeviceIdHash: await hashString(VoiceSettings.getVideoDeviceId()),
		screenShareAudioDeviceIdHash: await hashString(VoiceSettings.getScreenShareAudioDeviceId()),
		effectiveScreenShareAudioDeviceIdHash: await hashString(VoiceSettings.getEffectiveScreenShareAudioDeviceId()),
		inputVolume: VoiceSettings.getInputVolume(),
		outputVolume: VoiceSettings.getOutputVolume(),
		echoCancellation: VoiceSettings.getEchoCancellation(),
		noiseSuppression: VoiceSettings.getNoiseSuppression(),
		autoGainControl: VoiceSettings.getAutoGainControl(),
		deepFilterNoiseSuppression: VoiceSettings.getDeepFilterNoiseSuppression(),
		voiceProcessingMode: VoiceSettings.getVoiceProcessingMode(),
		vadThreshold: VoiceSettings.getVadThreshold(),
		vadAutoSensitivity: VoiceSettings.getVadAutoSensitivity(),
		vadEnhanced: VoiceSettings.getVadEnhanced(),
		cameraResolution: VoiceSettings.getCameraResolution(),
		screenshareResolution: VoiceSettings.getScreenshareResolution(),
		videoFrameRate: VoiceSettings.getVideoFrameRate(),
		streamingMode: VoiceSettings.getStreamingMode(),
		hideStreamPreview: VoiceSettings.getHideStreamPreview(),
		muteStreamAudio: VoiceSettings.getMuteStreamAudio(),
		shareAppAudio: VoiceSettings.getShareAppAudio(),
		shareDesktopAudio: VoiceSettings.getShareDesktopAudio(),
		shareDeviceAudio: VoiceSettings.getShareDeviceAudio(),
		streamHideCursor: VoiceSettings.getStreamHideCursor(),
		screenShareAudioSourceMode: VoiceSettings.getScreenShareAudioSourceMode(),
		screenShareAudioIncludeSources: VoiceSettings.getScreenShareAudioIncludeSources(),
		screenShareAudioExcludeSources: VoiceSettings.getScreenShareAudioExcludeSources(),
		preferredVideoCodec: VoiceSettings.getPreferredVideoCodec(),
		preferredScreenShareCodec: VoiceSettings.getPreferredScreenShareCodec(),
		screenShareContentHint: VoiceSettings.getScreenShareContentHint(),
		linuxAudioCapture: {
			workaround: VoiceSettings.getLinuxAudioCaptureWorkaround(),
			onlySpeakers: VoiceSettings.getLinuxAudioCaptureOnlySpeakers(),
			onlyDefaultSpeakers: VoiceSettings.getLinuxAudioCaptureOnlyDefaultSpeakers(),
			ignoreInputMedia: VoiceSettings.getLinuxAudioCaptureIgnoreInputMedia(),
			ignoreVirtual: VoiceSettings.getLinuxAudioCaptureIgnoreVirtual(),
			ignoreDevices: VoiceSettings.getLinuxAudioCaptureIgnoreDevices(),
			granularSelect: VoiceSettings.getLinuxAudioCaptureGranularSelect(),
			deviceSelect: VoiceSettings.getLinuxAudioCaptureDeviceSelect(),
		},
	};
}

function summarizeRoom(): Record<string, unknown> {
	const room = MediaEngine.room;
	const participant = room?.localParticipant as
		| {
				identity?: string;
				sid?: string;
				isMicrophoneEnabled?: boolean;
				isCameraEnabled?: boolean;
				isScreenShareEnabled?: boolean;
				audioTrackPublications?: Map<unknown, unknown>;
				videoTrackPublications?: Map<unknown, unknown>;
		  }
		| null
		| undefined;

	return {
		connected: MediaEngine.connected,
		connecting: MediaEngine.connecting,
		guildId: MediaEngine.guildId,
		channelId: MediaEngine.channelId,
		connectionId: MediaEngine.connectionId,
		voiceServerEndpointPresent: Boolean(MediaEngine.voiceServerEndpoint),
		roomState: room?.state ?? null,
		localParticipant: participant
			? {
					identity: participant.identity ?? null,
					sid: participant.sid ?? null,
					isMicrophoneEnabled: participant.isMicrophoneEnabled ?? null,
					isCameraEnabled: participant.isCameraEnabled ?? null,
					isScreenShareEnabled: participant.isScreenShareEnabled ?? null,
					audioPublicationCount: participant.audioTrackPublications?.size ?? null,
					videoPublicationCount: participant.videoTrackPublications?.size ?? null,
				}
			: null,
		voiceStats: MediaEngine.voiceStats,
		perTrackStats: MediaEngine.perTrackStats,
		statsTimeSeries: MediaEngine.statsTimeSeries,
		transport: {
			publisher: MediaEngine.publisherTransport,
			subscriber: MediaEngine.subscriberTransport,
		},
	};
}

function summarizeVirtmicNode(node: VirtmicNode): Record<string, string> {
	const result: Record<string, string> = {};
	for (const [key, value] of Object.entries(node)) {
		result[key] = value;
	}
	return result;
}

function summarizeNativeApp(app: NativeAudioApplication): Record<string, unknown> {
	return {
		pid: app.pid,
		identifier: app.identifier,
		name: app.name,
		audible: app.audible ?? null,
	};
}

async function collectDesktopMetadata(): Promise<Record<string, unknown>> {
	const electronApi = getElectronAPI();
	if (!electronApi) {
		return {isDesktop: false};
	}

	const electronRecord = electronApi as typeof electronApi & {
		isWaylandSession?: () => Promise<boolean>;
		getWaylandCapabilities?: () => Promise<unknown>;
	};

	const [
		desktopInfo,
		waylandSession,
		waylandCapabilities,
		diagnosticsPath,
		virtmicAvailability,
		virtmicTargets,
		nativeAvailability,
		nativeApps,
		troubleshooting,
	] = await Promise.all([
		withTimeout('desktop info', electronApi.getDesktopInfo()),
		electronRecord.isWaylandSession
			? withTimeout('wayland session', electronRecord.isWaylandSession())
			: Promise.resolve({error: 'isWaylandSession unavailable'}),
		electronRecord.getWaylandCapabilities
			? withTimeout('wayland capabilities', electronRecord.getWaylandCapabilities())
			: Promise.resolve({error: 'getWaylandCapabilities unavailable'}),
		electronApi.voiceDiagnostics
			? withTimeout('diagnostics path', electronApi.voiceDiagnostics.getPath())
			: Promise.resolve({error: 'voiceDiagnostics API unavailable'}),
		electronApi.virtmic
			? withTimeout('virtmic availability', electronApi.virtmic.getAvailability())
			: Promise.resolve({error: 'virtmic API unavailable'}),
		electronApi.virtmic
			? withTimeout('virtmic targets', electronApi.virtmic.listTargets({granular: true}))
			: Promise.resolve({error: 'virtmic API unavailable'}),
		electronApi.nativeAudio
			? withTimeout('native audio availability', electronApi.nativeAudio.getAvailability())
			: Promise.resolve({error: 'nativeAudio API unavailable'}),
		electronApi.nativeAudio
			? withTimeout('native audio apps', electronApi.nativeAudio.listAudibleApplications())
			: Promise.resolve({error: 'nativeAudio API unavailable'}),

		(() => {
			const cached = getCachedDesktopTroubleshootingSettings();
			if (cached) return Promise.resolve(cached);
			return withTimeout('desktop troubleshooting', getDesktopTroubleshootingSettings());
		})(),
	]);

	const virtmicTargetSummary =
		virtmicTargets && typeof virtmicTargets === 'object' && 'targets' in virtmicTargets
			? {
					...virtmicTargets,
					targets: Array.isArray(virtmicTargets.targets)
						? virtmicTargets.targets.slice(0, MAX_INCLUDED_TARGETS).map(summarizeVirtmicNode)
						: virtmicTargets.targets,
					truncated: Array.isArray(virtmicTargets.targets) && virtmicTargets.targets.length > MAX_INCLUDED_TARGETS,
				}
			: virtmicTargets;

	return {
		isDesktop: true,
		platform: electronApi.platform,
		desktopInfo,
		waylandSession,
		waylandCapabilities,
		diagnosticsPath,
		troubleshooting,
		virtmic: {
			availability: virtmicAvailability,
			targets: virtmicTargetSummary,
		},
		nativeAudio: {
			availability: nativeAvailability,
			audibleApplications: Array.isArray(nativeApps) ? nativeApps.map(summarizeNativeApp) : nativeApps,
			bridgeStats: getNativeAudioBridgeStats(),
		},
	};
}

async function collectMetadata(log: string): Promise<Record<string, unknown>> {
	const [nativePlatform, displayShareEnvironment, permissions, mediaDevices, voiceSettings, desktop, codecs] =
		await Promise.all([
			getNativePlatform(),
			getDisplayShareEnvironment().catch((error) => `error:${safeError(error).message ?? 'unknown'}`),
			Promise.all([
				queryPermission('microphone'),
				queryPermission('camera'),
				queryPermission('display-capture'),
				queryPermission('speaker-selection'),
			]),
			collectMediaDevices(),
			collectVoiceSettingsMetadata(),
			collectDesktopMetadata(),
			collectCodecMetadata(),
		]);

	const logBytes = bytes(log);
	const eventCount = log.trim().length === 0 ? 0 : log.trim().split('\n').length;
	const [microphone, camera, displayCapture, speakerSelection] = permissions;

	return {
		schemaVersion: 1,
		createdAt: new Date().toISOString(),
		log: {
			format: 'jsonl',
			maxBytes: 10 * 1024 * 1024,
			bytes: logBytes,
			eventCount,
		},
		app: {
			nativePlatform,
			displayShareEnvironment,
			isDesktop: isDesktop(),
			supportsDesktopScreenShareAudioCapture: supportsDesktopScreenShareAudioCapture(),
		},
		browser: collectBrowserMetadata(),
		webrtc: collectWebRtcMetadata(),
		codecs,
		permissions: {
			microphone,
			camera,
			displayCapture,
			speakerSelection,
		},
		mediaDevices,
		voiceSettings,
		voiceSession: summarizeRoom(),
		desktop,
		errorAggregates: getErrorAggregates(),
	};
}

function makeCrcTable(): Uint32Array {
	const table = new Uint32Array(256);
	for (let index = 0; index < 256; index += 1) {
		let value = index;
		for (let bit = 0; bit < 8; bit += 1) {
			value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
		}
		table[index] = value >>> 0;
	}
	return table;
}

const crcTable = makeCrcTable();

function crc32(data: Uint8Array): number {
	let crc = 0xffffffff;
	for (const byte of data) {
		crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
	}
	return (crc ^ 0xffffffff) >>> 0;
}

function dosDateTime(date: Date): {date: number; time: number} {
	const year = Math.max(1980, date.getFullYear());
	return {
		time: (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2),
		date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
	};
}

function writeUint16(view: DataView, offset: number, value: number): number {
	view.setUint16(offset, value, true);
	return offset + 2;
}

function writeUint32(view: DataView, offset: number, value: number): number {
	view.setUint32(offset, value >>> 0, true);
	return offset + 4;
}

function createZip(entries: Array<ZipEntry>): Uint8Array {
	const encoder = new TextEncoder();
	const now = dosDateTime(new Date());
	const encodedEntries = entries.map((entry) => {
		const name = encoder.encode(entry.name);
		const data = typeof entry.content === 'string' ? encoder.encode(entry.content) : entry.content;
		return {
			...entry,
			name,
			data,
			crc: crc32(data),
		};
	});

	const localParts: Array<Uint8Array> = [];
	const centralParts: Array<Uint8Array> = [];
	let offset = 0;

	for (const entry of encodedEntries) {
		const local = new Uint8Array(30 + entry.name.length + entry.data.length);
		const localView = new DataView(local.buffer);
		let cursor = 0;
		cursor = writeUint32(localView, cursor, 0x04034b50);
		cursor = writeUint16(localView, cursor, 20);
		cursor = writeUint16(localView, cursor, 0x0800);
		cursor = writeUint16(localView, cursor, 0);
		cursor = writeUint16(localView, cursor, now.time);
		cursor = writeUint16(localView, cursor, now.date);
		cursor = writeUint32(localView, cursor, entry.crc);
		cursor = writeUint32(localView, cursor, entry.data.length);
		cursor = writeUint32(localView, cursor, entry.data.length);
		cursor = writeUint16(localView, cursor, entry.name.length);
		cursor = writeUint16(localView, cursor, 0);
		local.set(entry.name, cursor);
		cursor += entry.name.length;
		local.set(entry.data, cursor);
		localParts.push(local);

		const central = new Uint8Array(46 + entry.name.length);
		const centralView = new DataView(central.buffer);
		cursor = 0;
		cursor = writeUint32(centralView, cursor, 0x02014b50);
		cursor = writeUint16(centralView, cursor, 20);
		cursor = writeUint16(centralView, cursor, 20);
		cursor = writeUint16(centralView, cursor, 0x0800);
		cursor = writeUint16(centralView, cursor, 0);
		cursor = writeUint16(centralView, cursor, now.time);
		cursor = writeUint16(centralView, cursor, now.date);
		cursor = writeUint32(centralView, cursor, entry.crc);
		cursor = writeUint32(centralView, cursor, entry.data.length);
		cursor = writeUint32(centralView, cursor, entry.data.length);
		cursor = writeUint16(centralView, cursor, entry.name.length);
		cursor = writeUint16(centralView, cursor, 0);
		cursor = writeUint16(centralView, cursor, 0);
		cursor = writeUint16(centralView, cursor, 0);
		cursor = writeUint16(centralView, cursor, 0);
		cursor = writeUint32(centralView, cursor, 0);
		cursor = writeUint32(centralView, cursor, offset);
		central.set(entry.name, cursor);
		centralParts.push(central);

		offset += local.length;
	}

	const centralDirectoryOffset = offset;
	const centralDirectorySize = centralParts.reduce((sum, part) => sum + part.length, 0);
	const end = new Uint8Array(22);
	const endView = new DataView(end.buffer);
	let cursor = 0;
	cursor = writeUint32(endView, cursor, 0x06054b50);
	cursor = writeUint16(endView, cursor, 0);
	cursor = writeUint16(endView, cursor, 0);
	cursor = writeUint16(endView, cursor, encodedEntries.length);
	cursor = writeUint16(endView, cursor, encodedEntries.length);
	cursor = writeUint32(endView, cursor, centralDirectorySize);
	cursor = writeUint32(endView, cursor, centralDirectoryOffset);
	writeUint16(endView, cursor, 0);

	const totalSize = offset + centralDirectorySize + end.length;
	const zip = new Uint8Array(totalSize);
	let writeOffset = 0;
	for (const part of [...localParts, ...centralParts, end]) {
		zip.set(part, writeOffset);
		writeOffset += part.length;
	}
	return zip;
}

function entryByteLength(entry: ZipEntry): number {
	return typeof entry.content === 'string' ? bytes(entry.content) : entry.content.byteLength;
}

export async function exportVoiceDiagnostics(): Promise<VoiceDiagnosticsUploadResponse> {
	diagnostics.info('export.requested');
	const log = await readVoiceDiagnosticsLog();
	const metadata = await collectMetadata(log);
	const stamp = nowStamp();
	const entries: Array<ZipEntry> = [
		{
			name: 'voice-diagnostics-log.jsonl',
			content: log.endsWith('\n') || log.length === 0 ? log : `${log}\n`,
			mimeType: 'application/jsonl',
		},
		{
			name: 'voice-diagnostics-metadata.json',
			content: `${JSON.stringify(metadata, null, 2)}\n`,
			mimeType: 'application/json',
		},
	];
	const zipBytes = createZip(entries);
	const zipBuffer = new ArrayBuffer(zipBytes.byteLength);
	new Uint8Array(zipBuffer).set(zipBytes);
	const blob = new Blob([zipBuffer], {type: 'application/zip'});
	const filename = `fluxer-voice-diagnostics-${stamp}.zip`;

	try {
		const result = await uploadVoiceDiagnosticsArchive(blob, filename);
		diagnostics.info('export.completed', {
			logBytes: bytes(log),
			zipBytes: zipBytes.byteLength,
			serverUploadId: result.id,
			serverUploadKey: result.key,
			serverCreatedAt: result.created_at,
			files: entries.map((entry) => ({name: entry.name, mimeType: entry.mimeType, bytes: entryByteLength(entry)})),
		});
		return result;
	} catch (error) {
		diagnostics.warn('export.failed', {
			logBytes: bytes(log),
			zipBytes: zipBytes.byteLength,
			error: safeError(error),
		});
		throw error;
	}
}

export async function exportVoiceDiagnosticsForCrash(
	report: VoiceDiagnosticCrashReport,
): Promise<VoiceDiagnosticsUploadResponse> {
	diagnostics.info('export.crash.requested', {
		reportId: report.id,
		previousSessionId: report.previousSession?.sessionId ?? null,
		minidumpCount: report.minidumps.length,
	});

	const electronApi = getElectronAPI();
	const dumpsApi = electronApi?.voiceDiagnostics;
	const minidumpEntries: Array<ZipEntry> = [];
	const minidumpManifest: Array<Record<string, unknown>> = [];

	if (dumpsApi?.readCrashMinidump) {
		for (const minidump of report.minidumps) {
			try {
				const data = await dumpsApi.readCrashMinidump(report.id, minidump.id);
				if (!data) {
					minidumpManifest.push({...minidump, included: false, reason: 'read-returned-null'});
					continue;
				}
				minidumpEntries.push({
					name: `crashdumps/${minidump.id}-${minidump.filename}`,
					content: data,
					mimeType: 'application/x-minidump',
				});
				minidumpManifest.push({...minidump, included: true, bytes: data.byteLength});
			} catch (error) {
				minidumpManifest.push({...minidump, included: false, reason: safeError(error).message ?? 'unknown'});
			}
		}
	} else {
		for (const minidump of report.minidumps) {
			minidumpManifest.push({...minidump, included: false, reason: 'electron-api-unavailable'});
		}
	}

	const log = await readVoiceDiagnosticsLog();
	const baseMetadata = await collectMetadata(log);
	const crashMetadata = {
		...baseMetadata,
		crash: {
			reportId: report.id,
			reportCreatedAt: report.createdAt,
			previousSession: report.previousSession,
			currentSession: report.currentSession,
			minidumps: minidumpManifest,
		},
	};

	const stamp = nowStamp();
	const entries: Array<ZipEntry> = [
		{
			name: 'voice-diagnostics-log.jsonl',
			content: log.endsWith('\n') || log.length === 0 ? log : `${log}\n`,
			mimeType: 'application/jsonl',
		},
		{
			name: 'voice-diagnostics-metadata.json',
			content: `${JSON.stringify(crashMetadata, null, 2)}\n`,
			mimeType: 'application/json',
		},
		...minidumpEntries,
	];
	const zipBytes = createZip(entries);
	const zipBuffer = new ArrayBuffer(zipBytes.byteLength);
	new Uint8Array(zipBuffer).set(zipBytes);
	const blob = new Blob([zipBuffer], {type: 'application/zip'});
	const filename = `fluxer-voice-diagnostics-crash-${stamp}.zip`;

	try {
		const result = await uploadVoiceDiagnosticsArchive(blob, filename);
		diagnostics.info('export.crash.completed', {
			reportId: report.id,
			minidumpsIncluded: minidumpEntries.length,
			zipBytes: zipBytes.byteLength,
			serverUploadId: result.id,
			serverUploadKey: result.key,
			serverCreatedAt: result.created_at,
		});

		try {
			await dumpsApi?.markCrashReportConsumed?.(report.id);
		} catch (error) {
			diagnostics.warn('export.crash.mark-consumed-failed', {reportId: report.id, error: safeError(error)});
		}
		return result;
	} catch (error) {
		diagnostics.warn('export.crash.failed', {
			reportId: report.id,
			zipBytes: zipBytes.byteLength,
			error: safeError(error),
		});
		throw error;
	}
}
